import React, { useState, useRef, useEffect } from 'react';
import { feedbackMap } from '../utils/utils';
import 'react-date-range/dist/styles.css';
import 'react-date-range/dist/theme/default.css';
import { DateRange, RangeKeyDict } from 'react-date-range';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { ConsoleLoggingListener } from 'microsoft-cognitiveservices-speech-sdk/distrib/lib/src/common.browser/ConsoleLoggingListener';
import { Cohort, InterviewObject, OrganizationInfo, User } from '@/utils/types';
import Rectangle from './ui/rectangle';
import LinearProgressWithLabel from './ui/progress';
import Typography from '@mui/material/Typography';
import Toolbar from '@mui/material/Toolbar';
import Box from '@mui/material/Box';
import { formatDateCustom } from '@/utils/dateFormatter';
import Avatar from '@mui/material/Avatar';
import { deepOrange, deepPurple } from '@mui/material/colors';
import TextField from '@mui/material/TextField';

interface AdminSettingsProps {
  orgInfo: OrganizationInfo | null | undefined,
  cohort: Cohort | undefined,
  admin: User,
  updateProfileInfo: (name: string, role: string) => void,
  setoverlayComp: (val: boolean) => void
}

function AdminSettings(props: AdminSettingsProps) {
  const [editProfile, seteditProfile] = useState<boolean>(false);
  const [profile, setprofile] = useState<any>({ name: props.admin.name, role: props.admin.role || '' });
  const [loading, setloading] = useState<boolean>(false);

  function getDate(date: string): Date | null {
    if (date !== undefined && date !== "" && date !== null) {
      const dateTimeArr = date.split(' ');
      const dateArr = dateTimeArr[0].split('-');
      const timeArr = dateTimeArr[1].split(':');
      return new Date(parseInt(dateArr[0]), parseInt(dateArr[1]) - 1, parseInt(dateArr[2]), parseInt(timeArr[0]), parseInt(timeArr[1]), parseInt(timeArr[2]));
    }
    return null;
  }

  const updateProfile = () => {
    if (profile.name !== props.admin.name || profile.role !== props.admin.role || '') {
      setloading(true);
      props.updateProfileInfo(profile.name, profile.role);
    }
    else {
      seteditProfile(false);
    }
  }

  const handleRoleChange = (event: { target: { value: any; }; }) => {
    setprofile({ ...profile, role: event.target.value }); // Updates state with the input value
  };

  const handleNameChange = (event: { target: { value: any; }; }) => {
    setprofile({ ...profile, name: event.target.value }); // Updates state with the input value
  };


  useEffect(() => {
    setloading(false);
    seteditProfile(false);
  }, [props.admin]);

  useEffect(() => {
    props.setoverlayComp(loading);
  }, [loading]);

  return (
    <div className="flex-col w-[100%] items-start">
      <div className='flex mb-4 items-center text-left w-[100%]'>
        <Avatar sx={{ minWidth: '56px', minHeight: '56px', bgcolor: deepOrange[500] }}>{props.admin.name.split(' ').map(word => word[0]).join('')}</Avatar>
      </div>
      <hr />
      {props.admin &&
        <div className='flex justify-between text-left w-[100%]  text-sm'>
          <div className='flex-col self-start w-[25%]'>
            <p className="font-bold">Personal Info</p>
            <p className="text-customBlack">Update your personal details</p>
          </div>
          <div className="flex-col gap-4 shadow-sm mb-5 ml-2 p-4 bg-white w-[75%]">
            <div className='flex-col self-start w-[75%]'>
              {!editProfile && <p className="text-customBlack">Name</p>}
              {editProfile && <TextField
                label="Name"
                variant="outlined"
                size="small"
                defaultValue={props.admin.name}
                onChange={handleNameChange} // Handles changes
                fullWidth
                sx={{ flex: 1 }} // Ensures equal width of the boxes
              />}
              {!editProfile && <p className="text-black ">{props.admin.name}</p>}
            </div>
            <div className='flex-col self-start'>
              {!editProfile && <p className="text-customBlack">Role</p>}
              {editProfile && <TextField
                label="Role"
                variant="outlined"
                size="small"
                defaultValue={props.admin.role || ''}
                onChange={handleRoleChange} // Handles changes
                fullWidth
                sx={{ flex: 1 }} // Ensures equal width of the boxes
              />}
              {!editProfile && <p className="text-black ">{props.admin.role || '-'}</p>}
            </div>
            <div className='flex-col self-start w-[75%]'>
              <p className="text-customBlack">Email</p>
              <p className="text-black ">{props.admin.email}</p>
            </div>
            <hr />
            {!editProfile && <div className='w-[unset] border border-customBlack self-end px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => seteditProfile(true)}><p>Edit</p></div>}
            {editProfile && <div className='flex justify-end gap-2'>
              <div className='px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => seteditProfile(false)}><p>Cancel</p></div>
              <div className='px-3 py-2 text-white cursor-pointer hover:bg-grayBlue-hover font-[300] bg-grayBlue rounded-md shadow-sm' onClick={() => updateProfile()}><p>Save Changes</p></div>
            </div>}
          </div>
        </div>}
      {props.orgInfo && props.cohort && props.admin.email === props.orgInfo.principalAdmin &&
        <div className='flex justify-between text-left w-[100%]  text-sm'>
          <div className='flex-col self-start w-[25%]'>
            <p className="font-bold">Principal Administrator Settings</p>
            <p className="text-customBlack">Manage your group plan information here</p>
          </div>
          <div className="flex-col gap-3 shadow-sm mb-5 ml-2 p-4 bg-white w-[75%]">
            <div className='flex text-left w-[100%]'>
              <div className="w-[35%]"><p className='mr-5 text-black'>Organization</p></div>
              <p className='text-customBlack'>{props.orgInfo.friendlyName}</p>
            </div>
            <div className='flex text-left w-[100%]'>
              <div className="w-[35%]"><p className='mr-5 text-black'>License Type</p></div>
              <p className='text-customBlack'>{props.cohort.licenseType}</p>
            </div>
            <div className='flex text-left w-[100%]'>
              <div className="w-[35%]"><p className='mr-5 text-black'>Licenses Included</p></div>
              <p className='text-customBlack'>{props.cohort.totalLicenses}</p>
            </div>
            <div className='flex  text-left w-[100%]'>
              <div className="w-[35%]"><p className='mr-5 text-black'>Plan Start Date</p></div>
              <p className='text-customBlack'>{formatDateCustom(props.cohort.startDate)}</p>
            </div>
            <div className='flex  text-left w-[100%]'>
              <div className="w-[35%]"><p className='mr-5 text-black'>Plan Renewal Date</p></div>
              <p className='text-customBlack'>{formatDateCustom(props.cohort.endDate)}</p>
            </div>
            <hr />
            <div className='w-[unset] border border-customBlack self-end px-3 py-2 cursor-pointer hover:bg-whiteButton-hover font-[300] bg-whiteButton rounded-md shadow-sm' onClick={() => window.location.href = 'mailto:info@thedevdifference.com?subject=Update%Plan'}><p>Contact to Cancel or Change Plan</p></div>
          </div>
        </div>}

    </div>
  );
}

export default AdminSettings;
